import React, { useEffect, useState, lazy, Suspense, useCallback } from "react";
import Header from "./../components/Header";
import Rating from "../components/homeComponents/Rating";
import { Link, useParams, useNavigate } from "react-router-dom";
import Message from "./../components/LoadingError/Error";
import { useDispatch, useSelector } from "react-redux";
import {
  createProductReview,
  listProductDetails,
} from "../Redux/Actions/ProductActions";
import { PRODUCT_CREATE_REVIEW_RESET } from "../Redux/Constants/ProductConstants";
import moment from "moment";
import "./sliderbuttons.css";
import "../App.css";
import { Helmet } from "react-helmet-async";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const Slider = lazy(() => import("react-slick"));

const sliderSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  lazyLoad: "ondemand",
};

const imageStyle = {
  width: "100%",
  height: "400px",
  objectFit: "cover",
  display: "block",
  borderRadius: "12px",
};

const cloudinaryOptimize = (url, width = 800) => {
  if (!url || !url.includes("cloudinary.com")) return url;
  return url.replace("/upload/", `/upload/w_${width},f_auto,q_auto/`);
};

/* ─────────────────────────────────────────────
   Full-page skeleton — mirrors the real layout
   so the container height never changes on load.
───────────────────────────────────────────── */
const ProductSkeleton = () => (
  <div className="row product-skeleton">
    {/* Left col — image placeholder */}
    <div className="col-md-6">
      <div className="sk-block sk-image" />
    </div>

    {/* Right col — text placeholders */}
    <div className="col-md-6 sk-info-col">
      <div className="sk-block sk-title" />
      <div className="sk-block sk-title-short" />

      <div className="sk-block sk-line" />
      <div className="sk-block sk-line" />
      <div className="sk-block sk-line sk-line-short" />

      <div className="sk-count-box">
        {/* Price row */}
        <div className="sk-count-row">
          <div className="sk-block sk-label" />
          <div className="sk-block sk-value" />
        </div>
        {/* Status row */}
        <div className="sk-count-row">
          <div className="sk-block sk-label" />
          <div className="sk-block sk-value" />
        </div>
        {/* Reviews row — sk-rating matches the star + text height */}
        <div className="sk-count-row">
          <div className="sk-block sk-label" />
          <div className="sk-block sk-rating" />
        </div>
        {/* Quantity row */}
        <div className="sk-count-row">
          <div className="sk-block sk-label" />
          <div className="sk-block sk-value sk-value-narrow" />
        </div>
      </div>

      <div className="sk-block sk-btn" />
    </div>
  </div>
);

const SingleProduct = () => {
  const [qty, setQty] = useState(1);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [reviewSuccess, setReviewSuccess] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [sliderLoaded, setSliderLoaded] = useState(false);

  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const productDetails = useSelector((state) => state.productDetails);
  const { loading, error, product } = productDetails;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const productReviewCreate = useSelector((state) => state.productReviewCreate);
  const { success: successCreateReview } = productReviewCreate;

  useEffect(() => {
    dispatch(listProductDetails(id));
  }, [dispatch, id]);

  useEffect(() => {
    setImgLoaded(false);
    setSliderLoaded(false);
  }, [id]);

  useEffect(() => {
    if (successCreateReview) {
      setReviewSuccess(true);
      setRating(0);
      setComment("");
      dispatch({ type: PRODUCT_CREATE_REVIEW_RESET });
      dispatch(listProductDetails(id));
      const timer = setTimeout(() => setReviewSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [successCreateReview, dispatch, id]);

  const AddToCartHandle = useCallback(
    (e) => {
      e.preventDefault();
      const price =
        product?.onSale && product?.salePrice
          ? Number(product.salePrice)
          : Number(product?.price);
      navigate(`/cart/${id}?qty=${qty}&price=${price}`);
    },
    [id, qty, navigate, product?.onSale, product?.salePrice, product?.price]
  );

  const submitHandler = useCallback(
    (e) => {
      e.preventDefault();
      dispatch(createProductReview(id, { rating, comment }));
    },
    [dispatch, id, rating, comment]
  );

  const displayPrice =
    product?.onSale && product?.salePrice
      ? Number(product.salePrice)
      : Number(product?.price);

  const rawProductImage = product?.images?.[0] || product?.image || "";
  const heroImage = cloudinaryOptimize(rawProductImage, 800);
  const ogImage = cloudinaryOptimize(rawProductImage, 1200);
  const productDescription = product?.description?.substring(0, 160) || "";
  const productUrl = `https://silentexchange.store/products/${id}`;
  const hasMultipleImages = product?.images?.length > 1;

  return (
    <>
      {product && (
        <Helmet>
          <title>{`${product.name} | Silent Exchange`}</title>
          <meta name="description" content={productDescription} />
          <meta
            name="keywords"
            content={`${product.name}, pokemon cards, sports cards, collectibles, trading cards, silent exchange`}
          />
          <link rel="canonical" href={productUrl} />
          {heroImage && (
            <link rel="preload" as="image" href={heroImage} fetchpriority="high" />
          )}
          <meta property="og:title" content={`${product.name} | Silent Exchange`} />
          <meta property="og:description" content={productDescription} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:url" content={productUrl} />
          <meta property="og:type" content="product" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={product.name} />
          <meta name="twitter:description" content={productDescription} />
          <meta name="twitter:image" content={ogImage} />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              name: product.name,
              image: heroImage,
              description: product.description,
              url: productUrl,
              offers: {
                "@type": "Offer",
                price: displayPrice.toFixed(2),
                priceCurrency: "USD",
                availability:
                  product.countInStock > 0
                    ? "https://schema.org/InStock"
                    : "https://schema.org/OutOfStock",
                url: productUrl,
              },
              ...(product.numReviews > 0 && {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: product.rating,
                  reviewCount: product.numReviews,
                },
              }),
            })}
          </script>
        </Helmet>
      )}

      <Header />
      <div className="container single-product">
        {error ? (
          <Message variant="alert-danger">{error}</Message>
        ) : loading || !product ? (
          <ProductSkeleton />
        ) : (
          <>
            <div className="row">
              {/* ── Images ── */}
              <div className="col-md-6">
                <div className="single-image-slider rounded">
                  {hasMultipleImages ? (
                    <Suspense
                      fallback={
                        <div className="img-skeleton-wrapper">
                          <div className="img-skeleton" />
                        </div>
                      }
                    >
                      <div className="img-skeleton-wrapper">
                        {!sliderLoaded && <div className="img-skeleton" />}
                        <div className={`img-fade-in${sliderLoaded ? " loaded" : ""}`}>
                          <Slider {...sliderSettings}>
                            {product.images.map((img, idx) => (
                              <div key={idx}>
                                <img
                                  src={cloudinaryOptimize(img, 800)}
                                  alt={`${product.name} ${idx + 1}`}
                                  loading={idx === 0 ? "eager" : "lazy"}
                                  fetchpriority={idx === 0 ? "high" : "low"}
                                  decoding={idx === 0 ? "sync" : "async"}
                                  style={imageStyle}
                                  onLoad={
                                    idx === 0 ? () => setSliderLoaded(true) : undefined
                                  }
                                />
                              </div>
                            ))}
                          </Slider>
                        </div>
                      </div>
                    </Suspense>
                  ) : (
                    <div className="img-skeleton-wrapper">
                      {!imgLoaded && <div className="img-skeleton" />}
                      <img
                        src={heroImage || "/fallback.jpg"}
                        alt={product.name}
                        loading="eager"
                        fetchpriority="high"
                        decoding="sync"
                        style={imageStyle}
                        className={`img-fade-in${imgLoaded ? " loaded" : ""}`}
                        onLoad={() => setImgLoaded(true)}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* ── Product Info ── */}
              <div className="col-md-6">
                <div className="product-dtl">
                  <div className="product-info">
                    <div className="product-name">{product.name}</div>
                  </div>
                  <p>{product.description}</p>

                  <div className="product-count col-lg-7">
                    <div className="flex-box d-flex justify-content-between align-items-center">
                      <h6>Price</h6>
                      {product.onSale && product.salePrice ? (
                        <>
                          <span className="text-danger fw-bold">
                            ${displayPrice.toFixed(2)}
                          </span>{" "}
                          <span className="text-muted text-decoration-line-through">
                            ${Number(product.price).toFixed(2)}
                          </span>
                        </>
                      ) : (
                        <span>${displayPrice.toFixed(2)}</span>
                      )}
                    </div>

                    <div className="flex-box d-flex justify-content-between align-items-center">
                      <h6>Status</h6>
                      {product.countInStock > 0 ? (
                        <span>In Stock</span>
                      ) : (
                        <span>Unavailable</span>
                      )}
                    </div>

                    <div className="flex-box d-flex justify-content-between align-items-center">
                      <h6>Reviews</h6>
                      <Rating
                        value={product.rating}
                        text={`${product.numReviews} reviews`}
                      />
                    </div>

                    {product.countInStock > 0 && (
                      <>
                        <div className="flex-box d-flex justify-content-between align-items-center">
                          <h6>Quantity</h6>
                          <select
                            value={qty}
                            onChange={(e) => setQty(e.target.value)}
                          >
                            {[...Array(product.countInStock).keys()].map((x) => (
                              <option key={x + 1} value={x + 1}>
                                {x + 1}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          onClick={AddToCartHandle}
                          className="round-black-btn"
                        >
                          Add To Cart
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Reviews ── */}
            <div className="row my-5">
              <div className="col-md-6">
                <h6 className="mb-3">REVIEWS</h6>
                {product.reviews.length === 0 && (
                  <Message variant="alert-info mt-3">No Reviews</Message>
                )}
                {product.reviews.map((review) => (
                  <div
                    key={review._id}
                    className="mb-5 mb-md-3 bg-light p-3 shadow-sm rounded"
                  >
                    <strong>{review.name}</strong>
                    <Rating value={review.rating} />
                    <span>{moment(review.createdAt).calendar()}</span>
                    <div className="alert alert-info mt-3">{review.comment}</div>
                  </div>
                ))}
              </div>

              <div className="col-md-6">
                <h6>WRITE A CUSTOMER REVIEW</h6>

                {reviewSuccess && (
                  <div className="alert alert-success">
                    Review submitted successfully!
                  </div>
                )}

                {userInfo ? (
                  <form onSubmit={submitHandler}>
                    <div className="my-4">
                      <strong>Rating</strong>
                      <select
                        value={rating}
                        onChange={(e) => setRating(e.target.value)}
                        className="col-12 bg-light p-3 mt-2 border-0 rounded"
                      >
                        <option value="">Select...</option>
                        <option value="1">1 - Poor</option>
                        <option value="2">2 - Fair</option>
                        <option value="3">3 - Good</option>
                        <option value="4">4 - Very Good</option>
                        <option value="5">5 - Excellent</option>
                      </select>
                    </div>
                    <div className="my-4">
                      <strong>Comment</strong>
                      <textarea
                        rows="3"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        className="col-12 bg-light p-3 mt-2 border-0 rounded"
                      />
                    </div>
                    <div className="my-3">
                      <button
                        type="submit"
                        className="col-12 bg-black border-0 p-3 rounded text-white"
                      >
                        SUBMIT
                      </button>
                    </div>
                  </form>
                ) : (
                  <Message variant="alert-warning">
                    Please <Link to="/login">Login</Link> to write a review
                  </Message>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SingleProduct;